import { isEmpty, isNaN } from 'lodash'
import { useState, useMemo, ReactElement, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { Button } from 'components/v2/Button'
import { googleMapsConfig } from 'config/google-maps'
import { useGoogleMapsScript } from 'hooks/google-maps'
import { isValidLatitude, isValidLongitude } from 'features/Places/utils'
import { IRouteStop } from 'features/Routes/types'
import { device } from 'utils/style'
import { adjustMapBounds, getGoogleDirectionsRoute } from 'utils/google-maps'
import { AdvancedMarker, InfoWindow, Map, useMap } from '@vis.gl/react-google-maps'
import { PinIcon } from 'components/Icons/PinIcon'
import { Polyline } from 'components/GoogleMaps/Polyline'

const Container = styled.div`
  height: 100%;
  position: relative;
  border-radius: 16px;
  height: 400px;
  overflow: hidden;

  @media ${device.laptop} {
    height: 100%;
  }
`
const ButtonContainer = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
`

const StopName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #28265d;
`

interface RouteStopInfoWindowProps {
  stop: IRouteStop
  onCloseClick: () => void
}

const RouteStopInfoWindow = ({ stop, onCloseClick }: RouteStopInfoWindowProps): ReactElement => {
  return (
    <InfoWindow position={stop.position} onCloseClick={onCloseClick}>
      <StopName>{stop.name}</StopName>
    </InfoWindow>
  )
}

interface RouteStopMarkerProps {
  stop: IRouteStop
  onClick?: () => void
}

const RouteStopMarker = ({ stop, onClick }: RouteStopMarkerProps): ReactElement | null => {
  if (isNaN(stop.position.lat) || isNaN(stop.position.lng)) return null

  return (
    <AdvancedMarker position={stop.position} title={stop.name} onClick={onClick}>
      <PinIcon />
    </AdvancedMarker>
  )
}

interface RouteMapProps {
  encodedShapePath: string
  onCalculateShape: (directions: google.maps.DirectionsRoute, outboundDirections?: google.maps.DirectionsRoute) => void
  stops: IRouteStop[]
  mapCenterLocation: {
    lat: number
    lng: number
  }
  onDistance?: (distance: number) => void
  outboundStops?: IRouteStop[]
  onCalculateShapeError: (error: TSFixMe) => void
}

export const RouteMap = ({
  stops,
  mapCenterLocation,
  onCalculateShape,
  onCalculateShapeError,
  encodedShapePath,
  outboundStops
}: RouteMapProps): ReactElement | null => {
  const [isCalculatingShape, setIsCalculatingShape] = useState(false)
  const { isLoaded, geometryLib } = useGoogleMapsScript()
  const { defaultCenter, defaultDirectionsRendererOptions, defaultZoom } = googleMapsConfig

  const [selected, setSelected] = useState<IRouteStop | null>(null)
  const mapInstance = useMap()

  const shapePath = useMemo(() => {
    if (!isLoaded) return []
    return (
      geometryLib?.encoding.decodePath(encodedShapePath).map((point) => {
        return { lat: point.lat(), lng: point.lng() }
      }) || []
    )
  }, [encodedShapePath, isLoaded, geometryLib])

  const calculateShape = async (): Promise<void> => {
    const validStops = stops.filter((stop) => isValidLatitude(String(stop.position.lat)))
    if (validStops.length < 2) return
    setIsCalculatingShape(true)

    try {
      const inboundDirectionsRoute = await getGoogleDirectionsRoute(validStops)
      let outboundDirectionsRoute
      if (outboundStops?.length) {
        outboundDirectionsRoute = await getGoogleDirectionsRoute(outboundStops)
      }
      onCalculateShape(inboundDirectionsRoute, outboundDirectionsRoute)
    } catch (error) {
      onCalculateShapeError(error)
    }
    setIsCalculatingShape(false)
  }

  const centerCoordinates = useMemo(() => {
    if (isValidLatitude(String(mapCenterLocation.lat)) && isValidLongitude(String(mapCenterLocation.lng))) {
      return { lat: mapCenterLocation.lat, lng: mapCenterLocation.lng }
    }
    return { lat: defaultCenter.LAT, lng: defaultCenter.LNG }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapCenterLocation.lat, mapCenterLocation.lng])

  useEffect(() => {
    if (!mapInstance) return

    const validStops = stops.filter((stop) => isValidLatitude(String(stop.position.lat)))
    if (validStops.length > 0) {
      adjustMapBounds(
        validStops.map((stop) => {
          return { lat: stop.position.lat, lng: stop.position.lng }
        }),
        mapInstance
      )
    } else if (shapePath.length > 1) {
      adjustMapBounds(shapePath, mapInstance)
    }
  }, [mapInstance, stops, shapePath])

  if (!isLoaded) return null

  return (
    <Container>
      <Map
        mapId="place-map"
        defaultZoom={defaultZoom}
        defaultCenter={centerCoordinates}
        {...googleMapsConfig.defaultOptions}
      >
        {stops.map((stop) => (
          <RouteStopMarker key={stop.id} stop={stop} onClick={(): void => setSelected(stop)} />
        ))}
        {selected && <RouteStopInfoWindow stop={selected} onCloseClick={(): void => setSelected(null)} />}
        <Polyline
          path={shapePath}
          options={{
            geodesic: true,
            ...defaultDirectionsRendererOptions.polylineOptions
          }}
        />
      </Map>
      <ButtonContainer>
        <Button
          kind="secondary"
          type="button"
          onClick={calculateShape}
          disabled={stops.length < 2 || !isEmpty(encodedShapePath)}
          loading={isCalculatingShape}
        >
          <FormattedMessage id="routes.calculateShape" defaultMessage="Calculate Shape" />
        </Button>
      </ButtonContainer>
    </Container>
  )
}
